import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import StoreAvatarImage from "../customImages/StoreAvatarImage";
import Modal from "./Modal";
import StoreSearchInput from "./StoreSearchInput";
import { useDebounce } from "../../hooks/useDebounce";
import { searchStores } from "../../services/search/searchStores";
import CloseModalButton from "./closeModalButton";

interface StoreSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  placeholder?: string;
  onSelectStore?: (store: any) => void;
}

const StoreSearchPopup: React.FC<StoreSearchPopupProps> = ({
  isOpen,
  onClose,
  title,
  placeholder,
  onSelectStore,
}) => {
  const router = useRouter();
  const { t } = useTranslation("hero");

  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const debouncedSearchText = useDebounce(searchText, 400);

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchText.trim()) {
        setResults([]);
        setHasSearched(false);
        return;
      }

      setIsLoading(true);
      setHasSearched(true);

      try {
        const result = await searchStores({ keyword: debouncedSearchText, page: 1 });
        setResults(result?.stores || []);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchText]);

  const handleSelectStore = (store: any) => {
    if (onSelectStore) {
      onSelectStore(store);
    } else {
      router.push(`/categories/${store?.category}/${store?.slug}`);
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full md:w-[90%] lg:w-[70%] mx-auto">
        <div className="mb-6 w-full">
          <h3 className="text-base md:text-2xl lg:text-3xl w-full font-bold text-[#233377] flex items-center gap-3 justify-start text-left rtl:text-left ">
            <Image src="/images/ri_instagram-fill.svg" alt="star icon" width={28} height={28} />
            {title}
          </h3>
        </div>

        <div className="mb-6">
          <StoreSearchInput
            value={searchText}
            onChange={setSearchText}
            placeholder={placeholder || t("search_store")}
            className="w-full"
          />
          {isLoading && (
            <div className="mt-2 text-sm text-gray-500 text-center">{t("searching")}</div>
          )}
        </div>

        <div className="mb-6">
          {results && results.length > 0 && (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {results.map((store, index) => (
                <div
                  key={`${store?.slug}-${index}-${store?.id}`}
                  className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors shadow-sm"
                  onClick={() => handleSelectStore(store)}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-orange-300 flex-shrink-0">
                    <StoreAvatarImage
                      image={store?.imageUrl}
                      alt="store logo"
                      size={48}
                      className="w-full h-full"
                      fallbackPath="avatar.png"
                    />
                  </div>
                  <div className="flex-1 min-w-0 text-left rtl:text-right">
                    <span className="font-bold text-[#353535] block truncate">
                      {store?.name?.split("_").join(" ")}
                    </span>
                    {store?.platform && (
                      <span className="text-sm text-gray-500">{store.platform}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasSearched && !isLoading && (!results || results.length === 0) && (
            <div className="p-10 bg-gray-50 rounded-lg shadow-sm">
              <div className="text-center text-gray-500">{t("no_results_found")}</div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default StoreSearchPopup;
